import React, { useState } from "react";
import { makeGetApiCall, makePostApiCall } from "../../shared/makeApiCall";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import ProgressModal from "../../components/modals/ProgressModal";
import SunShippingLabelReviewModal from "../../components/forms/shipping/SunShippingLabelReviewModal";
import SunBolReviewModal from "../../components/forms/shipping/SunBolReviewModal";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import { generateUniqueNumber } from "../../shared/utility";
import { SelectOption, SunOrderFormValues } from "../../types/sunOrders";

const normalizeSunProduct = (product: any) => ({
  value: product.value,
  label: product.label,
  id: product.value,
  name_en: product.label,
  description_en: "",
  plu: `SUN-${product.value}`,
  unit_of_measure: "Case",
  selling_unit_of_measure: "Case",
  weight: "1",
  individual_unit_size: "",
});

const emptyPackagingItem = { product_lot: "" };

export function useSunOrderFormViewModel(
  logout: () => void,
  addSnackbar: (snackbar: SnackbarData) => void,
  orderId?: string
) {
  const [customers, setCustomers] = useState<SelectOption[]>([]);
  const [sunCustomers, setSunCustomers] = useState<SelectOption[]>([]);
  const [employeeOptions, setEmployeeOptions] = useState<SelectOption[]>([]);
  const [order, setOrder] = useState<SunOrderFormValues | null>(null);
  const [loading, setLoading] = useState(false);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null as any,
  });
  const [slideOver, setSlideOver] = useState({
    open: false,
    childComponent: null as any,
    customClass: null as string | null,
  });

  const showErrorModal = (title: string, subtitle?: string) => {
    setEmptyModelOpen({
      childComponent: React.createElement(ModelErrorComponent, {
        setOpen: setEmptyModelOpen,
        subtitle,
        title,
      }),
      open: true,
    });
  };

  const showProgressModal = (message = "Processing...") => {
    setEmptyModelOpen({
      childComponent: React.createElement(ProgressModal, { message }),
      open: true,
    });
  };

  const hideProgressModal = () => {
    setEmptyModelOpen({
      childComponent: null,
      open: false,
    });
  };

  const getCustomersForDropdown = async () => {
    const callResult: any = await makeGetApiCall(`getSunCustomersForDropdown`);
    if (callResult.success) {
      setCustomers(callResult.data);
      return callResult;
    }

    if (callResult.errorStatus === 401) {
      logout();
    }
    showErrorModal(callResult.data?.message ?? "Could not load customers");
    return callResult;
  };

  const getSunCustomersForDropdown = async () => {
    const callResult: any = await makeGetApiCall(`getSunCustomersForDropdown`);
    if (callResult.success) {
      setSunCustomers(callResult.data);
      return callResult;
    }

    if (callResult.errorStatus === 401) {
      logout();
    }
    showErrorModal(callResult.data?.message ?? "Could not load ship-to customers");
    return callResult;
  };

  const getEmployeesForDropdown = async (type: number[], department: number[]) => {
    const payload = { department, type };
    const callResult: any = await makePostApiCall(`employeesForDropdown`, payload);

    if (callResult.success) {
      setEmployeeOptions(callResult.data);
      return;
    }

    if (callResult.errorStatus === 401) {
      logout();
    }
  };

  const getMyProducts = async (search: string) => {
    const callResult: any = await makeGetApiCall(`getSunProductsForDropdown`);
    if (callResult.success) {
      return callResult.data
        .map(normalizeSunProduct)
        .filter((product: any) =>
          !search ? true : product.label.toLowerCase().includes(search.toLowerCase())
        );
    }

    if (callResult.errorStatus === 401) {
      logout();
    }
    showErrorModal(callResult.data?.message ?? "Could not load SUN products");
    return [];
  };

  const getOrderDetails = async () => {
    if (!orderId) return;

    setLoading(true);
    const callResult: any = await makePostApiCall(`fetchSunOrder/${orderId}`);
    if (callResult.success) {
      setOrder(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data?.message ?? "Could not load SUN order");
    }
    setLoading(false);
  };

  const createOrderApiCall = async (newOrder: SunOrderFormValues) => {
    const callResult: any = await makePostApiCall(`createSunOrder`, newOrder);
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Sun order created",
          SnackbarType.Success
        )
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data?.message ?? "Could not create SUN order");
    }
    return callResult;
  };

  const updateOrderApiCall = async (newOrder: SunOrderFormValues) => {
    if (!orderId) return { success: false };

    const callResult: any = await makePostApiCall(`updateSunOrder/${orderId}`, newOrder);
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data?.message ?? "Sun order updated",
          SnackbarType.Success
        )
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data?.message ?? "Could not update SUN order");
    }
    return callResult;
  };

  const onUpdateOrderItem = async (
    id: number,
    payload: { quantity?: string | number; sscc_hri_number?: string | null }
  ) => {
    const callResult: any = await makePostApiCall(`updateSunOrderItem/${id}`, payload);

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Sun order item updated",
          SnackbarType.Success
        )
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data?.message ?? "Could not update item");
    }

    return callResult;
  };

  const updateOrderItemUnitCost = async (orderItemId: number, unitPrice: number) => {
    const callResult: any = await makePostApiCall(`updateSunOrderItemUnitCost`, {
      order_item_id: orderItemId,
      unit_price: parseFloat(`${unitPrice}`),
    });

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Unit price updated",
          SnackbarType.Success
        )
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data?.message ?? "Could not update unit price");
    }

    return callResult;
  };

  const deleteOrderItemApiCall = async (id: number) => {
    const callResult: any = await makePostApiCall(`deleteSunOrderItem/${id}`);
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data?.message ?? "Sun order item deleted",
          SnackbarType.Success
        )
      );
      setEmptyModelOpen({ open: false, childComponent: null });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data?.message ?? "Could not delete item");
    }
    return callResult;
  };

  const addProductToOrderApiCall = async (currentOrderId: number, payload: any) => {
    const callResult: any = await makePostApiCall(
      `addProductToSunOrder/${currentOrderId}`,
      payload
    );
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Product added!",
          SnackbarType.Success
        )
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data?.message ?? "Could not add product");
    }
    return callResult;
  };

  const buildExclusiveShippingLabelData = (info: any) => ({
    label_type: info.label_type,
    sun_order_id: info.sun_order_id,
    sun_order_item_id: info.sun_order_item_id,
    product_lot: "",
    date_code: "",
    ship_from: { ...info.ship_from },
    ship_to: { ...info.ship_to },
    po_number: info.po_number ?? "",
    location_id: info.location_id ?? "",
    customer_item_number: info.customer_item_number ?? "",
    carrier: info.carrier ?? "",
    bill_of_lading: info.bill_of_lading ?? "",
    pro_number: info.pro_number ?? "",
  });

  const fetchExclusiveShippingLabelInfo = async (
    sunOrderId: number,
    sunOrderItemId: number
  ) =>
    makePostApiCall(`sunDistribution/exclusiveSunOrderGetShippingLabelInfo`, {
      sun_order_id: sunOrderId,
      sun_order_item_id: sunOrderItemId,
    });

  const printExclusiveShippingLabel = async (labelData: any) =>
    makePostApiCall(`sunDistribution/exclusiveSunOrderPrintShippingLabel`, {
      label_data: labelData,
    });

  const openSunShippingLabelReview = async (orderItem: any) => {
    const sunOrderId = parseInt(`${orderId ?? order?.id ?? 0}`, 10);
    if (!sunOrderId || !orderItem?.id) {
      showErrorModal("Save the order before printing a shipping label");
      return;
    }

    showProgressModal("Loading shipping label...");
    const infoResult: any = await fetchExclusiveShippingLabelInfo(
      sunOrderId,
      orderItem.id
    );
    hideProgressModal();

    if (!infoResult.success) {
      if (infoResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(
        infoResult.data?.message ?? "Could not load shipping label info"
      );
      return;
    }

    const initialLabelData = buildExclusiveShippingLabelData(infoResult.data);

    const resetLabelData = async () => {
      const resetResult: any = await fetchExclusiveShippingLabelInfo(
        sunOrderId,
        orderItem.id
      );
      if (!resetResult.success) {
        showErrorModal(
          resetResult.data?.message ?? "Could not reset shipping label info"
        );
        return null;
      }
      return buildExclusiveShippingLabelData(resetResult.data);
    };

    setSlideOver({
      open: true,
      customClass: "max-w-5xl",
      childComponent: React.createElement(SunShippingLabelReviewModal, {
        setSlideOver,
        labelInfo: infoResult.data,
        initialLabelData,
        orderItem,
        packagingItem: emptyPackagingItem,
        isPrinting: false,
        onReset: resetLabelData,
        onPrint: async (labelData: any) => {
          showProgressModal("Printing shipping label...");
          const printResult: any = await printExclusiveShippingLabel(labelData);
          hideProgressModal();

          if (printResult.success) {
            window.open(printResult.data.location, "_blank");
            setSlideOver({
              childComponent: null,
              open: false,
              customClass: null,
            });
          } else {
            if (printResult.errorStatus === 401) {
              logout();
            }
            showErrorModal(
              printResult.data?.message ?? "Could not print shipping label"
            );
          }
        },
      }),
    });
  };

  const fetchExclusiveBolInfo = async (sunOrderId: number) =>
    makePostApiCall(`sunDistribution/exclusiveSunOrderGetBolInfo`, {
      sun_order_id: sunOrderId,
    });

  const printExclusiveBol = async (bolData: any) =>
    makePostApiCall(`sunDistribution/exclusiveSunOrderPrintBol`, {
      bol_data: bolData,
    });

  const openSunBolReview = async (formValues?: SunOrderFormValues) => {
    const sunOrderId = parseInt(`${orderId ?? formValues?.id ?? order?.id ?? 0}`, 10);
    if (!sunOrderId) {
      showErrorModal("Save the order before generating a BOL");
      return;
    }

    if (formValues) {
      const updateResult: any = await updateOrderApiCall(formValues);
      if (!updateResult.success) {
        return;
      }
    }

    showProgressModal("Loading Bill of Lading...");
    const infoResult: any = await fetchExclusiveBolInfo(sunOrderId);
    hideProgressModal();

    if (!infoResult.success) {
      if (infoResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(
        infoResult.data?.message ?? "Could not load Bill of Lading info"
      );
      return;
    }

    const initialBolData = infoResult.data;

    const resetBolData = async () => {
      const resetResult: any = await fetchExclusiveBolInfo(sunOrderId);
      if (!resetResult.success) {
        showErrorModal(
          resetResult.data?.message ?? "Could not reset Bill of Lading info"
        );
        return null;
      }
      return resetResult.data;
    };

    setSlideOver({
      open: true,
      customClass: "max-w-7xl",
      childComponent: React.createElement(SunBolReviewModal, {
        setSlideOver,
        initialBolData,
        isPrinting: false,
        onReset: resetBolData,
        onPrint: async (bolData: any) => {
          showProgressModal("Generating BOL document...");
          const printResult: any = await printExclusiveBol(bolData);
          hideProgressModal();

          if (printResult.success) {
            window.open(printResult.data.location, "_blank");
            setSlideOver({
              childComponent: null,
              open: false,
              customClass: null,
            });
          } else {
            if (printResult.errorStatus === 401) {
              logout();
            }
            showErrorModal(
              printResult.data?.message ?? "Could not generate Bill of Lading"
            );
          }
        },
      }),
    });
  };

  const createSunOrderInvoice = async (sunOrderId?: number) => {
    const id = parseInt(`${sunOrderId ?? orderId ?? order?.id ?? 0}`, 10);
    if (!id) {
      showErrorModal("Save the order before creating an invoice");
      return { success: false };
    }

    showProgressModal("Creating QuickBooks invoice...");
    const callResult: any = await makePostApiCall(
      `sunDistribution/exclusiveSunOrderCreateInvoiceQb`,
      { sun_order_id: id }
    );
    hideProgressModal();

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data?.message ?? "Invoice created",
          SnackbarType.Success
        )
      );
      setOrder((current) =>
        current
          ? {
              ...current,
              invoice_number: callResult.data?.invoice_number ?? current.invoice_number,
              sun_quickbooks_invoice_id:
                callResult.data?.sun_quickbooks_invoice_id ??
                current.sun_quickbooks_invoice_id,
            }
          : current
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data?.message ?? "Could not create invoice");
    }

    return callResult;
  };

  return {
    customers,
    sunCustomers,
    employeeOptions,
    order,
    loading,
    emptyModal,
    setEmptyModelOpen,
    showErrorModal,
    slideOver,
    setSlideOver,
    getCustomersForDropdown,
    getSunCustomersForDropdown,
    getEmployeesForDropdown,
    getMyProducts,
    getOrderDetails,
    createOrderApiCall,
    updateOrderApiCall,
    onUpdateOrderItem,
    updateOrderItemUnitCost,
    deleteOrderItemApiCall,
    addProductToOrderApiCall,
    openSunShippingLabelReview,
    openSunBolReview,
    createSunOrderInvoice,
  };
}
